import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../../src/utils/axios";
import useAuth from "../../../hooks/useAuth";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Link,
} from "@mui/material";
import { CheckCircle, XCircle, UserPlus, Calendar, Users } from "react-feather";
import { alpha } from "@mui/material/styles";

// GraphQL query to check team invite
const CHECK_TEAM_INVITE_QUERY = `
  query CheckTeamInvite($token: String!) {
    checkTeamInvite(token: $token) {
      id
      userId
      email
      role
      status
      inviteToken
      inviteExpires
      team {
        id
        name
        logo
      }
    }
  }
`;

// GraphQL mutation to accept team invite
const ACCEPT_TEAM_INVITE_MUTATION = `
  mutation AcceptTeamInvite($input: AcceptTeamInviteInput!) {
    acceptTeamInvite(input: $input) {
      id
      name
      members {
        id
        userId
        email
        role
        status
      }
    }
  }
`;

const TeamInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, signIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [acceptingInvite, setAcceptingInvite] = useState(false);

  // Check the invite on component mount
  useEffect(() => {
    const checkInvite = async () => {
      try {
        setLoading(true);

        if (!token) {
          throw new Error("Invalid invite link. No token provided.");
        }

        const response = await axiosInstance.post("/graphql", {
          query: CHECK_TEAM_INVITE_QUERY,
          variables: { token },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        const inviteInfo =
          response.data.checkTeamInvite || response.data.data?.checkTeamInvite;

        if (!inviteInfo) {
          throw new Error("Invalid or expired invitation");
        }

        setInviteData(inviteInfo);
      } catch (err) {
        console.error("Error checking invite:", err);
        setError(err.message || "Failed to check invitation");
      } finally {
        setLoading(false);
      }
    };

    checkInvite();
  }, [token]);

  // Handle accepting the invite
  const handleAcceptInvite = async () => {
    try {
      setAcceptingInvite(true);

      const response = await axiosInstance.post("/graphql", {
        query: ACCEPT_TEAM_INVITE_MUTATION,
        variables: {
          input: { token },
        },
      });

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      setSuccess(true);

      // Redirect to teams page after a delay
      setTimeout(() => {
        navigate("/calendar/teams");
      }, 3000);
    } catch (err) {
      console.error("Error accepting invite:", err);
      setError(err.message || "Failed to accept invitation");
    } finally {
      setAcceptingInvite(false);
    }
  };

  // Handle decline invitation
  const handleDeclineInvite = () => {
    navigate("/calendar/teams");
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/calendar/teams")}
          fullWidth
        >
          Go to Teams
        </Button>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={2}
          sx={{
            textAlign: "center",
            p: 4,
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.success.light}`,
            background: (theme) => alpha(theme.palette.success.light, 0.1),
          }}
        >
          <CheckCircle
            color="green"
            size={60}
            style={{ marginBottom: "1rem" }}
          />
          <Typography variant="h4" gutterBottom>
            Invitation Accepted!
          </Typography>
          <Typography variant="body1" paragraph>
            You have successfully joined the team "{inviteData?.team?.name}".
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Redirecting to teams page...
          </Typography>
          <CircularProgress size={24} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            py: 3,
            px: 4,
            background: (theme) => alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Typography variant="h4" gutterBottom>
            Team Invitation
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            You've been invited to join a team
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                mr: 3,
              }}
            >
              {inviteData?.team?.logo ? (
                <img
                  src={inviteData.team.logo}
                  alt={inviteData.team.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <Users size={30} color="#666" />
              )}
            </Box>
            <Box>
              <Typography variant="h5" gutterBottom>
                {inviteData?.team?.name || "Team"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Role:{" "}
                {inviteData?.role === "admin" ? "Administrator" : "Member"}
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ my: 3 }}>
            <Typography variant="body1">
              You've been invited to join{" "}
              <strong>{inviteData?.team?.name}</strong> as a {inviteData?.role}.
              {inviteData?.role === "admin" &&
                " You will have administrative privileges for this team."}
            </Typography>
          </Alert>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<XCircle />}
              onClick={handleDeclineInvite}
              disabled={acceptingInvite}
              sx={{ width: "48%" }}
            >
              Decline
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                acceptingInvite ? <CircularProgress size={20} /> : <UserPlus />
              }
              onClick={handleAcceptInvite}
              disabled={acceptingInvite}
              sx={{ width: "48%" }}
            >
              {acceptingInvite ? "Accepting..." : "Accept Invitation"}
            </Button>
          </Box>

          {!isAuthenticated && (
            <Alert severity="warning" sx={{ mt: 4 }}>
              <AlertTitle>Not logged in</AlertTitle>
              You are not currently logged in. You'll need to
              <Link href="/auth/sign-in" sx={{ mx: 1 }}>
                sign in
              </Link>
              or create an account to join this team.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeamInvite;
